import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useTheme } from "../../contexts/ThemeContext"
import { useTranslation } from "../../i18n/LanguageContext"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"
import assetInMeet25 from '@/assets/inMeet25.svg'
import {
  selectTotalUnreadChats,
  useMessengerStore,
} from "@/features/messenger/store/messenger.store"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { APP_ROUTES } from "@/shared/constants/routes.constants"
import { navActiveItem, type NavItem } from "./nav-active-item"

// Custom animation styles
const customStyles = `
  @keyframes slowPulse {
    0%, 100% {
      opacity: 0.15;
      transform: scale(1);
    }
    50% {
      opacity: 0.25;
      transform: scale(1.05);
    }
  }
  
  @keyframes iconBounce {
    0%, 100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(-3px);
    }
  }
  
  @keyframes slideIn {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }
  
  @keyframes bubblePulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
  }
`

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const t = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, toggleTheme } = useTheme()
  const [isFullscreen, setIsFullscreen] = useState(false)
  // Live derivation — receiving a message (app-wide subscription) bumps unread;
  // marking a chat read clears it. Both re-run this selector and update the badge.
  // Unread is computed against MY participant row, keyed by contactPresenceId.
  const myUserId = useAuthStore((s) => s.presenceId) ?? null
  const chatCount = useMessengerStore((s) => selectTotalUnreadChats(s, myUserId))

  // Inject custom styles
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = customStyles
    document.head.appendChild(style)
    return () => style.remove()
  }, [])
  
  const active = navActiveItem(location.pathname)

  const NAV: { id: NavItem; label: string; icon: string; onClickNavigate: string }[] = [
    {
      id: "meetings", label: t("nav.meetings"),
      onClickNavigate: '/meetings',
      icon: '/meetings.svg',
    },
    {
      id: "chat", label: t("nav.chats"),
      onClickNavigate: '/chats',
      icon: '/icons/chats.svg',
    },
    {
      id: "webinar", label: t("nav.webinar"),
      onClickNavigate: '/webinar',
      icon: '/icons/webinars.svg',
    },
    {
      id: "calendar", label: t("nav.calendar"),
      onClickNavigate: '/calendar',
      icon: '/icons/calender.svg',
    },
    // {
    //   id: "analytics", label: t("nav.analytics"),
    //   onClickNavigate: '/analytics',
    //   icon: '/icons/analytics.svg',
    // },
    // {
    //   id: "rooms", label: "Rooms",
    //   onClickNavigate: '/rooms',
    //   icon: '/icons/roomsfilledNew.svg',
    // },
    // {
    //   id: "marketplace", label: t("nav.marketplace"),
    //   onClickNavigate: '/market',
    //   icon: '/icons/marketplaceiconfilledNew.svg',
    // },
    {
      id: "settings", label: t("nav.settings"),
      onClickNavigate: '/settings',
      icon: '/icons/settings.svg',
    },
  ]

  const handleNavClick = (item: typeof NAV[0]) => {
    // Presence owns its own Chats/Broadcast/Calls/Settings sub-nav, so landing
    // on /chats while already there (e.g. from its Settings tab) needs a
    // signal beyond the path: a fresh `at` so a second click, still on
    // /chats, is still a change the page's effect can react to.
    if (item.id === "chat") {
      navigate(item.onClickNavigate, { state: { presenceTab: "chats", at: Date.now() } })
    } else {
      navigate(item.onClickNavigate)
    }
    // Only close sidebar on mobile (< 1024px)
    if (window.innerWidth < 1024) {
      onClose()
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange)
  }, [])

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden transition-all duration-300 ease-in-out"
          onClick={onClose}
          style={{ animation: "fadeIn 0.3s ease-out" }}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:relative
        inset-y-0 left-0
        h-full
        flex flex-col
        shadow-2xl lg:shadow-xl
        z-50
        transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0 opacity-100' : '-translate-x-full lg:translate-x-0 opacity-0 lg:opacity-100'}
        w-[80px]
        lg:rounded-xl
        overflow-visible lg:overflow-hidden
        ${isOpen ? 'lg:translate-x-0' : 'lg:-translate-x-full'}
      `}>
        {/* Modern Gradient Background (light) / Solid card (dark) */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 dark:hidden" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent dark:hidden" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-500/10 via-transparent to-transparent dark:hidden" />
        <div className="absolute inset-0 hidden dark:block bg-card" />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col h-full">
        {/* Logo & Close Button */}
        <div className="flex items-center justify-center p-4 border-b border-white/10">
          {/* The logo is the usual way back to the dashboard from any section
              (Settings, Webinar, Calendar), but it used to be a plain image
              with no cursor or action. */}
          <button
            type="button"
            onClick={() => {
              navigate(APP_ROUTES.DASHBOARD_HOME)
              if (window.innerWidth < 1024) {
                onClose()
              }
            }}
            aria-label={t('common.dashboard')}
            title={t('common.dashboard')}
            className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            <img src={assetInMeet25} alt="Inmeet" />
          </button>

          {/* Mobile close button - half outside the rail */}
          <button
            onClick={onClose}
            className="lg:hidden absolute -right-4 top-2 w-5 h-5 flex items-center justify-center rounded-full bg-card/95 text-white shadow-lg border border-white/30 dark:bg-slate-900/95 dark:border-slate-700 hover:scale-105 transition-transform"
            aria-label={t('common.closeMenu')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-slate-800 dark:text-slate-200">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 gap-3 flex flex-col justify-center overflow-y-auto scrollbar-hide">
          {NAV.map(item => (
            <Tooltip key={item.id} delayDuration={200}>
              <TooltipTrigger asChild>
                <button
                  id={`tour-nav-${item.id === 'chat' ? 'chat' : item.id}`}
                  className={`
                    nav-btn w-full flex items-center justify-center px-2 py-4 rounded-xl
                    transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                    relative group
                    ${active === item.id 
                      ? "text-white shadow-lg scale-105 bg-card/15 dark:bg-card/5 dark:border-white/10 backdrop-blur-md" 
                      : "text-white/80 hover:text-white hover:scale-110 hover:bg-card/10 hover:backdrop-blur-sm"
                    }
                  `}
                  onClick={() => handleNavClick(item)}
                >
                  {/* Active indicator left border */}
                  {active === item.id && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-card rounded-r-full animate-[slideIn_0.3s_ease-out]" />
                  )}
                  <div className={`
                    flex items-center justify-center w-6 h-6 rounded-lg
                    transition-all duration-300
                    ${active === item.id ? "animate-icon-float" : "group-hover:animate-[iconBounce_0.3s_ease-in-out]"}
                  `}>
                    <img 
                      src={item?.icon} 
                      alt={item.label} 
                      className={`
                        transition-transform duration-300 group-hover:scale-110
                        ${item.id === 'marketplace' || item.id === 'settings' ? 'w-7 h-7' : 'w-6 h-6'}
                      `} 
                    />
                  </div>
                  {/* Bubble count for chat (outside the animated icon to prevent position shifts) */}
                  {item.id === 'chat' && chatCount > 0 && (
                    <span className="absolute -top-1 -right-1 z-20 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full animate-[bubblePulse_2s_ease-in-out_infinite] shadow-lg dark:border-card">
                      {chatCount > 99 ? '99+' : chatCount}
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-900 text-white border border-slate-700">
                {item.label}
              </TooltipContent>
            </Tooltip>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="p-3 border-t border-white/10 space-y-2">
          {/* Theme Toggle */}
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <button
                onClick={toggleTheme}
                className="group w-full flex items-center justify-center p-2 rounded-xl text-white/80 hover:bg-card/10 hover:text-white transition-all duration-200"
                aria-label={t('common.toggleTheme')}
              >
                <div className="w-7 h-7 flex items-center justify-center transition-transform duration-300 group-hover:animate-[iconBounce_0.3s_ease-in-out] group-hover:scale-110">
                  {theme === 'light' ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="5"/>
                      <line x1="12" y1="1" x2="12" y2="3"/>
                      <line x1="12" y1="21" x2="12" y2="23"/>
                      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                      <line x1="1" y1="12" x2="3" y2="12"/>
                      <line x1="21" y1="12" x2="23" y2="12"/>
                      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                    </svg>
                  )}
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-slate-900 text-white border border-slate-700">
              {theme === 'light' ? t('sidebar.darkMode') : t('sidebar.lightMode')}
            </TooltipContent>
          </Tooltip>

          {/* Fullscreen Toggle */}
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <button
                onClick={toggleFullscreen}
                className="group w-full flex items-center justify-center p-2 rounded-xl text-white/80 hover:bg-card/10 hover:text-white transition-all duration-200"
                aria-label={t('common.toggleFullscreen')}
              >
                <div className="w-7 h-7 flex items-center justify-center transition-transform duration-300 group-hover:animate-[iconBounce_0.3s_ease-in-out] group-hover:scale-110">
                  {isFullscreen ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16v3a2 2 0 0 0 2 2h3"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
                    </svg>
                  )}
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-slate-900 text-white border border-slate-700">
              {isFullscreen ? t('sidebar.exitFullscreen') : t('sidebar.enterFullscreen')}
            </TooltipContent>
          </Tooltip>
        </div>
        </div>
      </div>
    </>
  )
}
